import React, { useState, useEffect } from 'react';
import { 
  Container, Box, TextField, Button, Typography, Card, Alert, CircularProgress, 
  Stepper, Step, StepLabel, useTheme, useMediaQuery, InputAdornment, IconButton 
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { registerPartner, clearError } from '../../redux/partnerSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { PhoneInput } from '../../components/PhoneInput';
import LocationPicker from '../../components/LocationPicker';
import TimePicker from '../../components/TimePicker';
import { 
  validateEmail, validatePhone, validatePassword, validateGSTIN, 
  validatePAN, validateRequired, validatePincode, validateIFSC 
} from '../../utils/validators';
import { fetchLocationFromPincode, isValidPincode } from '../../utils/addressUtils';
import axios from 'axios';
import PublicHeader from '../../components/Layout/PublicHeader';

const steps = ['Business & Address', 'Bank Details', 'Gym Location'];

export const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isLoading, error } = useSelector((state: RootState) => state.partner);
  
  const [activeStep, setActiveStep] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [checkingExistence, setCheckingExistence] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    business_name: '',
    business_address: '',
    gstin: '',
    pan: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_account_holder_name: '',
    gym_name: '',
    gym_address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: 0,
    longitude: 0,
    opening_time: '06:00:00',
    closing_time: '22:00:00',
    total_capacity: ''
  });

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    let newValue: string | number = value;
    
    if (type === 'number') {
      // Keep as string if empty or just typing, convert to number when valid
      newValue = value === '' ? '' : parseFloat(value) || '';
    }
    
    setFormData({ ...formData, [name]: newValue });
    
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handlePincodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const pincode = e.target.value;
    setFormData({ ...formData, pincode });
    
    if (fieldErrors.pincode) {
      setFieldErrors({ ...fieldErrors, pincode: '' });
    }

    if (isValidPincode(pincode)) {
      setPincodeLoading(true);
      const location = await fetchLocationFromPincode(pincode);
      setPincodeLoading(false);
      
      if (location) {
        setFormData(prev => ({
          ...prev,
          city: location.city,
          state: location.state
        }));
      }
    }
  };

  const checkEmailPhoneExists = async (email: string, phone: string): Promise<boolean> => {
    try {
      const response = await axios.post<{ exists: boolean }>('http://localhost:3000/api/v1/partners/check-exists', {
        email,
        phone
      });
      return response.data.exists;
    } catch (error) {
      console.error('Error checking existence:', error);
      return false;
    }
  };

  const validateStep = async (step: number): Promise<boolean> => {
    const errors: Record<string, string> = {};

    if (step === 0) {
      const nameError = validateRequired(formData.name, 'Name');
      if (nameError) errors.name = nameError;

      const emailError = validateEmail(formData.email);
      if (emailError) errors.email = emailError;

      const phoneError = validatePhone(formData.phone);
      if (phoneError) errors.phone = phoneError;

      const passwordError = validatePassword(formData.password);
      if (passwordError) errors.password = passwordError;

      const businessNameError = validateRequired(formData.business_name, 'Business Name');
      if (businessNameError) errors.business_name = businessNameError;

      const businessAddressError = validateRequired(formData.business_address, 'Business Address');
      if (businessAddressError) errors.business_address = businessAddressError;

      const pincodeError = validatePincode(formData.pincode);
      if (pincodeError) errors.pincode = pincodeError;

      if (formData.gstin) {
        const gstinError = validateGSTIN(formData.gstin);
        if (gstinError) errors.gstin = gstinError;
      }

      if (formData.pan) {
        const panError = validatePAN(formData.pan);
        if (panError) errors.pan = panError;
      }

      if (Object.keys(errors).length === 0) {
        setCheckingExistence(true);
        const exists = await checkEmailPhoneExists(formData.email, formData.phone);
        setCheckingExistence(false);

        if (exists) {
          errors.email = 'Email or phone already registered';
          errors.phone = 'Email or phone already registered';
        }
      }
    }

    if (step === 1) {
      if (formData.bank_ifsc) {
        const ifscError = validateIFSC(formData.bank_ifsc);
        if (ifscError) errors.bank_ifsc = ifscError;
      }
    }

    if (step === 2) {
      const gymNameError = validateRequired(formData.gym_name, 'Gym Name');
      if (gymNameError) errors.gym_name = gymNameError;

      const gymAddressError = validateRequired(formData.gym_address, 'Gym Address');
      if (gymAddressError) errors.gym_address = gymAddressError;

      if (formData.latitude === 0 || formData.longitude === 0) {
        errors.gym_address = 'Please select gym location on the map';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    const isValid = await validateStep(activeStep);
    if (isValid && activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateStep(activeStep);
    if (!isValid) return;

    const result = await dispatch(registerPartner(formData));
    if (result.type === registerPartner.fulfilled.type) {
      // Skip OTP verification in development - auto-redirect to dashboard
      if (process.env.NODE_ENV === 'development') {
        navigate('/dashboard');
      } else {
        navigate('/verify-otp', { state: { phone: formData.phone } });
      }
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setFormData({
      ...formData,
      latitude: location.lat,
      longitude: location.lng,
      gym_address: location.address
    });
    if (fieldErrors.gym_address) {
      setFieldErrors({ ...fieldErrors, gym_address: '' });
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Personal & Business Details
            </Typography>

            <TextField
              fullWidth
              label="Your Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              margin="normal"
              required
            />

            <PhoneInput
              value={formData.phone}
              onChange={(value: string) => setFormData({ ...formData, phone: value })}
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              required
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password || 'Min 8 chars, 1 uppercase, 1 number, 1 special char'}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Business Information
            </Typography>

            <TextField
              fullWidth
              label="Business Name"
              name="business_name"
              value={formData.business_name}
              onChange={handleChange}
              error={!!fieldErrors.business_name}
              helperText={fieldErrors.business_name}
              margin="normal"
              required
            />

            <TextField
              fullWidth
              label="Business Address"
              name="business_address"
              value={formData.business_address}
              onChange={handleChange}
              error={!!fieldErrors.business_address}
              helperText={fieldErrors.business_address}
              margin="normal"
              multiline
              rows={2}
              required
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handlePincodeChange}
                error={!!fieldErrors.pincode}
                helperText={fieldErrors.pincode || 'Enter pincode to auto-fill city & state'}
                margin="normal"
                required
                InputProps={{
                  endAdornment: pincodeLoading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  readOnly: true
                }}
              />
            </Box>

            <TextField
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                readOnly: true
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="GSTIN (Optional)"
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                error={!!fieldErrors.gstin}
                helperText={fieldErrors.gstin || 'Format: 22AAAAA0000A1Z5'}
                margin="normal"
              />

              <TextField
                fullWidth
                label="PAN (Optional)"
                name="pan"
                value={formData.pan}
                onChange={handleChange}
                error={!!fieldErrors.pan}
                helperText={fieldErrors.pan || 'Format: ABCDE1234F'}
                margin="normal"
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Bank Details (Optional)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Add your bank details to receive payments. You can skip this and add later.
            </Typography>

            <TextField
              fullWidth
              label="Account Holder Name"
              name="bank_account_holder_name"
              value={formData.bank_account_holder_name}
              onChange={handleChange}
              margin="normal"
            />

            <TextField
              fullWidth
              label="Account Number"
              name="bank_account_number"
              value={formData.bank_account_number}
              onChange={handleChange}
              margin="normal"
            />

            <TextField
              fullWidth
              label="IFSC Code"
              name="bank_ifsc"
              value={formData.bank_ifsc}
              onChange={handleChange}
              error={!!fieldErrors.bank_ifsc}
              helperText={fieldErrors.bank_ifsc || 'Format: SBIN0001234'}
              margin="normal"
            />
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
              Gym Location & Details
            </Typography>

            <TextField
              fullWidth
              label="Gym Name"
              name="gym_name"
              value={formData.gym_name}
              onChange={handleChange}
              error={!!fieldErrors.gym_name}
              helperText={fieldErrors.gym_name}
              margin="normal"
              required
            />

            <Box sx={{ my: 3 }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                Select Gym Location on Map
              </Typography>
              <LocationPicker
                apiKey={googleMapsApiKey}
                onLocationSelect={handleLocationSelect}
                initialLocation={
                  formData.latitude && formData.longitude
                    ? { lat: formData.latitude, lng: formData.longitude }
                    : undefined
                }
              />
              {fieldErrors.gym_address && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 1, display: 'block' }}>
                  {fieldErrors.gym_address}
                </Typography>
              )}
            </Box>

            <TextField
              fullWidth
              label="Gym Address"
              name="gym_address"
              value={formData.gym_address}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={2}
              required
              helperText="Auto-filled from map or type manually"
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mt: 3 }}>
              <TimePicker
                label="Opening Time"
                value={formData.opening_time}
                onChange={(value) => setFormData({ ...formData, opening_time: value })}
                required
              />

              <TimePicker
                label="Closing Time"
                value={formData.closing_time}
                onChange={(value) => setFormData({ ...formData, closing_time: value })}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Total Capacity"
              name="total_capacity"
              type="number"
              value={formData.total_capacity}
              onChange={handleChange}
              required
              placeholder="Enter gym capacity"
              inputProps={{
                min: 1,
                max: 1000
              }}
              sx={{
                mt: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: '#f8f9fa'
                  }
                }
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <PublicHeader activeLink="signup" />
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 'calc(100vh - 60px)',
          py: { xs: 3, sm: 4 }
        }}>
        <Card sx={{ 
          p: { xs: 3, sm: 4 }, 
          width: '100%',
          boxShadow: { xs: 2, sm: 3 }
        }}>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}
          >
            Partner Registration
          </Typography>

          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: 4,
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: { xs: 500, sm: 500 },
                lineHeight: 1.3
              },
              '& .MuiStepIcon-root': {
                fontSize: { xs: '1.75rem', sm: '2rem' }
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: '#14B8A6',
                fontSize: { xs: '2rem', sm: '2.25rem' }
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: '#14B8A6'
              },
              '& .MuiStepConnector-line': {
                borderTopWidth: { xs: '2px', sm: '3px' }
              },
              '& .MuiStep-root': {
                px: { xs: 0.5, sm: 1 }
              }
            }}
            orientation="horizontal"
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={activeStep === steps.length - 1 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
            {renderStepContent()}

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: 4,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 }
            }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ 
                  minWidth: { xs: '100%', sm: '120px' },
                  height: '48px',
                  fontSize: { xs: '1rem', sm: '0.875rem' },
                  fontWeight: 500,
                  order: { xs: 2, sm: 1 }
                }}
              >
                Back
              </Button>

              <Button
                variant="contained"
                type="submit"
                disabled={isLoading || checkingExistence}
                sx={{ 
                  minWidth: { xs: '100%', sm: '120px' },
                  height: '48px',
                  fontSize: { xs: '1rem', sm: '0.875rem' },
                  fontWeight: 600,
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  },
                  '&:disabled': {
                    backgroundColor: '#B0E0DC'
                  },
                  order: { xs: 1, sm: 2 }
                }}
              >
                {isLoading || checkingExistence ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : activeStep === steps.length - 1 ? (
                  'Complete Registration'
                ) : (
                  'Next'
                )}
              </Button>
            </Box>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
              Already have an account?{' '}
              <Typography
                component="span"
                onClick={() => navigate('/login')}
                sx={{ 
                  cursor: 'pointer', 
                  color: '#14B8A6', 
                  fontWeight: 'bold',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Login here
              </Typography>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Container>
    </>
  );
};

export default SignupPage;
